import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { IUserProfile } from '../interfaces';
import AuthService from '../services/AuthService';
import TokenService from '../services/TokenService';
import { isCockpitAllowedRole } from '../constants/UserRoles';

interface AuthContextProps {
  user: IUserProfile | null;
  setUser: (user: IUserProfile | null) => void;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  showAccountSetupModal: boolean;
  setShowAccountSetupModal: (show: boolean) => void;
}

const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { t } = useTranslation();
  const [user, setUser] = useState<IUserProfile | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showAccountSetupModal, setShowAccountSetupModal] = useState<boolean>(false);

  const logout = useCallback(() => {
    TokenService.clearTokens();
    setUser(null);
    setIsAuthenticated(false);
    setShowAccountSetupModal(false);
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      if (TokenService.getAccessToken()) {
        try {
          const profile = await AuthService.me();

          if (!isCockpitAllowedRole(profile.role)) {
            logout();
            setIsLoading(false);
            return;
          }

          setUser(profile);
          setIsAuthenticated(true);

          // Check if account setup is completed
          if (!profile.account_setup_completed) {
            setShowAccountSetupModal(true);
          }
        } catch (error) {
          console.error('Failed to fetch user profile', error);
          setIsAuthenticated(false);
        }
      }
      setIsLoading(false);
    };

    fetchUser();
  }, [logout]);

  const login = async (email: string, password: string) => {
    await AuthService.login(email, password);
    const profile = await AuthService.me();

    if (!isCockpitAllowedRole(profile.role)) {
      logout();
      throw new Error(t('auth.cockpitAccessDenied'));
    }

    setUser(profile);
    setIsAuthenticated(true);

    // After login, check if account setup is completed
    if (!profile.account_setup_completed) {
      setShowAccountSetupModal(true);
    }
  };

  return (
    <AuthContext.Provider
      value={{ 
        user, 
        setUser, 
        login, 
        logout, 
        isAuthenticated, 
        isLoading, 
        showAccountSetupModal, 
        setShowAccountSetupModal 
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextProps => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
